import React, { useState, useEffect } from 'react';
import { Button, Checkbox, Grid, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useParams } from 'react-router-dom';
import MainCard from 'ui-component/cards/MainCard';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import { get, post, put } from '../axios/apiService';
import toast from 'react-hot-toast';
import { isEmpty } from 'utils/validator';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';

function AddEditRoleForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [role, setRole] = useState({
    name: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isValidation, setIsValidation] = useState(false);
  const { hasWritePermission } = usePermissions();
  const canEdit = hasWritePermission(MODULE_KEYS.Role_Management);

  const rows = modules;

  const columns = [
    { field: 'name', headerName: 'Module Name', flex: 1 },
    {
      field: 'canRead',
      headerName: 'Read',
      flex: 1,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.canRead || false}
          onChange={(e) => handleCheckboxChange(params.row.id, 'canRead', e)}
          disabled={!canEdit}
        />
      )
    },
    {
      field: 'canWrite',
      headerName: 'Write',
      flex: 1,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.canWrite || false}
          onChange={(e) => handleCheckboxChange(params.row.id, 'canWrite', e)}
          disabled={!canEdit}
        />
      )
    }
  ];

  const handleCheckboxChange = (moduleId, field, event) => {
    if (!canEdit) return

    const updatedRows = rows.map(row =>
      row.id === moduleId ? { ...row, [field]: event.target.checked } : row
    );
    setModules(updatedRows);
  };

  useEffect(() => {
    const getAllModules = async () => {
      setLoading(true);
      try {
        const response = await get('AdminModules', null);
        if (response?.values) {
          const modulesWithRights = response.map(module => ({
            id: module.id,
            name: module.name,
            canRead: false,
            canWrite: false
          }));
          setModules(modulesWithRights);
        }
      } catch (err) {
        console.error(err?.message);
        toast.error(err?.response?.data || 'Failed to load modules');
      } finally {
        setLoading(false);
      }
    };

    if (id == null || id == undefined) {
      getAllModules();
    }
  }, []);

  useEffect(() => {
    if (id != undefined && id != null) {
      getByIdRole();
    }
  }, [id]);

  useEffect(() => {
    if (isValidation) {
      validator();
    }
  }, [role.name, role.description]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setRole((prevRole) => ({
      ...prevRole,
      [name]: value
    }));
  };

  const validator = () => {
    const errors = {};
    if (isEmpty(role?.name)) {
      errors.name = 'Please enter a role name';
    }
    if (isEmpty(role?.description)) {
      errors.description = 'Please enter a description';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    setIsValidation(true);
    if (!validator()) return;

    const apiUrl = id != undefined && id != null ? `AdminRoles/${id}` : 'AdminRoles';

    try {
      setLoading(true);

      const roleModulePermissions = modules
        .filter(x => x.canRead || x.canWrite)
        .map(x => ({
          adminModuleId: x.id,
          canRead: x.canRead,
          canWrite: x.canWrite,
          name: x.name
        }));

      const param = {
        name: role.name,
        description: role.description,
        roleModulePermissions
      };

      if (id) {
        param.id = id;
      }

      const response = id
        ? await put(apiUrl, param)
        : await post(apiUrl, param);

      if (response != null) {
        toast.success(id ? 'Role updated successfully' : 'Role added successfully');
        navigate('/role-management');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error?.response?.data || 'Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  const getByIdRole = async () => {
    setLoading(true);
    try {
      const response = await get(`AdminRoles/${id}`, null);
      if (response != null) {
        setRole({
          name: response?.name || '',
          description: response?.description || ''
        });

        const modulesWithPermissions = response?.roleModulePermissions?.map((x) => ({
          id: x?.adminModuleId,
          name: x?.name,
          canRead: x?.canRead,
          canWrite: x?.canWrite
        }));
        setModules(modulesWithPermissions);
      }
    } catch (err) {
      console.error(err.message);
      toast.error(err?.response?.data || 'Failed to load role');
      navigate('/role-management');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <MainCard
        title={id ? canEdit ? 'Edit Admin Role' : "View Admin Role" : 'Add Admin Role'}
        backButton={{ onClick: () => navigate('/role-management') }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <TextField
              fullWidth
              label="Role Name *"
              name="name"
              type="text"
              value={role.name}
              onChange={handleChange}
              disabled={!canEdit}
            />
            <div style={{ color: 'red' }}>{formErrors.name}</div>
          </Grid>
          <Grid item xs={12} sm={6} md={8} lg={8}>
            <TextField
              fullWidth
              label="Description *"
              name="description"
              type="text"
              value={role.description}
              onChange={handleChange}
              disabled={!canEdit}
            />
            <div style={{ color: 'red' }}>{formErrors.description}</div>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ height: 500, marginTop: '20px' }}>
          <DataGrid
            getRowId={(row) => row?.id}
            rows={rows}
            columns={columns}
            hideFooter
            disableSelectionOnClick
          />
        </Grid>

        {
          canEdit && (
            <Button
              variant="contained"
              sx={{ marginTop: '20px', padding: '5px 30px' }}
              onClick={handleSubmit}
              disabled={!canEdit}
            >
              {id ? 'Update' : 'Save'}
            </Button>
          )}

      </MainCard>
      <LoadingBackdrop open={isLoading} />
    </div>
  );
}

export default AddEditRoleForm;